import { FileCheck2 } from "lucide-react";
import { useState, useCallback, useMemo, memo, useEffect, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { Check, Article as DocIcon } from "@mui/icons-material";

import axios from "axios";
import paths from "../../routes/paths";
import { endpoints } from "../../api/endpoint";
import { EmiWrapperComponent } from "../emiCalculator";
import { BackButton, NextButton } from "../../components/buttons";
import DocumentSection from "../preOnboarding/financierSections/DocumentSection";
import {
  useGetDriverLead,
  useUpdateDriverLead,
  useGetDriverLeadDetails,
} from "../../hooks/use-driver";
import {
  FINANCIER_TYPES,
  DOCUMENTS_BY_FINANCIER,
} from "../preOnboarding/constants/documents.config";
import { useAuth } from "../../contexts/AuthContext";
import SuccessDialog from "../preOnboarding/SuccessDialog";
import ErrorState from "../../components/errorState/ErrorState";
import FullScreenLoader from "../../components/fullScreenLoader";
import { ONBOARDING_STATUS } from "../onboarding/config/tableConfig";
import { createDriverPreOnboarding } from "../../api/modules/preOnboarding";
import { uploadAllPreOnboardingFiles } from "../../utils/uploadPreOnboardingFiles";
import DocumentChecklist from "../preOnboarding/documentChecklist/DocumentChecklist";
import { mapDocumentsToUploadedFiles } from "../preOnboarding/utils/mapDocumentsToUploadedFiles";

const STEPS = ["Document Checklist", "Upload Documents"];

const flattenDocuments = (sections) =>
  sections.flatMap((section) =>
    (section.documents || []).map((doc) => ({
      id: doc.id,
      name: doc.name,
      uploadType: doc.uploadType,
      optional: doc.requirementType === "OPTIONAL",
      requirementType: doc.requirementType,
      rule: doc.rule,
    })),
  );

const shouldRequireDocument = (rule, context) => {
  if (!rule) return true;

  if (rule.anyOf) {
    return rule.anyOf.some((condition) => {
      if (condition.field === "cibilScore" && condition.lessThan) {
        return context.cibilScore && context.cibilScore < condition.lessThan;
      }
      if (
        condition.field === "ageException" &&
        condition.equals !== undefined
      ) {
        return context.ageException === condition.equals;
      }
      if (condition.field === "vehicleFinanced" && condition.equals) {
        return context.vehicleFinanced === condition.equals;
      }
      return false;
    });
  }

  if (rule.field && rule.equals !== undefined) {
    if (rule.field === "vehicleFinanced")
      return context.vehicleFinanced === rule.equals;
    if (rule.field === "ageException")
      return context.ageException === rule.equals;
    if (rule.field === "cibilScore" && rule.lessThan)
      return context.cibilScore && context.cibilScore < rule.lessThan;
  }

  return true;
};

const getRequiredDocuments = (financierType, context) => {
  const sections = DOCUMENTS_BY_FINANCIER[financierType];
  if (!sections) return [];
  return flattenDocuments(sections).filter(
    (doc) => !doc.optional && shouldRequireDocument(doc.rule, context),
  );
};
// -------------------------------------------------------------

const transformDocumentsToPayload = (uploadedFiles, uploadedDocuments) => {
  const payload = {};

  Object.entries(uploadedFiles).forEach(([documentId, files]) => {
    const uploaded = uploadedDocuments[documentId];

    if (files.front || files.back) {
      payload[documentId] = {
        frontUrl: uploaded?.frontUrl || files.front?.url,
        backUrl: uploaded?.backUrl || files.back?.url,
      };
    } else if (files.multiple && files.multiple.length > 0) {
      payload[documentId] = {
        urls: uploaded?.urls || files.multiple.map((f) => f.url),
      };
    } else if (files.single) {
      payload[documentId] = { url: uploaded?.url || files.single.url };
    }
  });

  return payload;
};

const LeadDocumentUpload = memo(({ onSubmitSuccess }) => {
  const navigate = useNavigate();

  const { leadId } = useParams();

  const { user } = useAuth();
  const userEmail = user?.email || "Unknown User";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [financierType, setFinancierType] = useState(null);

  const [activeStep, setActiveStep] = useState(0);
  const [acknowledged, setAcknowledged] = useState(false);

  const [uploadedFiles, setUploadedFiles] = useState({});
  const [documentErrors, setDocumentErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    prn: "",
    driverName: "",
  });

  const [leadData, setLeadData] = useState(null);

  const {
    data: leadResponse,
    isLoading: isLoadingLead,
    isError: isLeadFetchError,
    error: leadFetchError,
  } = useGetDriverLeadDetails(leadId);

  const { mutate: updateDriverLead } = useUpdateDriverLead(leadId);

  useEffect(() => {
    if (isLoadingLead || !leadResponse) {
      return;
    }

    setLeadData(leadResponse);
    setFinancierType(leadResponse?.loanSummary?.financierInternal);

    if (leadResponse?.onboardingStatus === ONBOARDING_STATUS.NOT_STARTED) {
      updateLeadStatus();
    }

    const adhaarDoc = {
      aadhaar: {
        frontUrl: leadResponse?.documentUpload?.aadhaarFrontUrl || null,
        backUrl: leadResponse?.documentUpload?.aadhaarBackUrl || null,
      },
      pan: {
        url: leadResponse?.documentUpload?.panCardUrl || null,
      },
      rc: {
        frontUrl: leadResponse?.documentUpload?.rcFrontUrl || null,
        backUrl: leadResponse?.documentUpload?.rcBackUrl || null,
      },
    };

    setUploadedFiles(
      mapDocumentsToUploadedFiles(leadResponse ? adhaarDoc : {}),
    );
  }, [leadResponse, isLoadingLead]);

  const updateLeadStatus = () => {
    return new Promise((resolve, reject) => {
      updateDriverLead({
        onboardingStatus: ONBOARDING_STATUS.IN_PROGRESS,
        updatedBy: userEmail,
      });
    });
  };

  const documentContext = {
    cibilScore: leadData?.cibilScore ?? null,
    vehicleFinanced: leadData?.vehicleFinanced,
    ageException: leadData?.age > 55 || leadData?.age < 18,
  };

  const requiredDocuments = useMemo(
    () => getRequiredDocuments(financierType, documentContext),
    [financierType, documentContext],
  );

  const handleDocumentUpload = useCallback((documentId, files, side = null) => {
    setUploadedFiles((prev) => {
      const current = prev[documentId] || {};
      let updated;

      if (side === "front" || side === "back") {
        updated = { ...current, [side]: files[0] };
      } else if (side === "multiple") {
        updated = {
          ...current,
          multiple: [...(current.multiple || []), ...files],
        };
      } else {
        updated = { ...current, single: files[0] };
      }

      return { ...prev, [documentId]: updated };
    });

    setDocumentErrors((prev) => {
      if (!prev[documentId]) return prev;
      const next = { ...prev };
      delete next[documentId];
      return next;
    });
  }, []);

  const validateDocuments = useCallback(() => {
    const newErrors = {};

    requiredDocuments.forEach((doc) => {
      const uploadedFile = uploadedFiles[doc.id];

      if (!uploadedFile) {
        newErrors[doc.id] = `${doc.name} is required`;
      } else if (doc.uploadType === "both") {
        if (!uploadedFile.front || !uploadedFile.back) {
          newErrors[doc.id] = `Both front and back of ${doc.name} are required`;
        }
      } else if (doc.uploadType === "multiple") {
        if (!uploadedFile.multiple || uploadedFile.multiple.length === 0) {
          newErrors[doc.id] = `At least one file for ${doc.name} is required`;
        }
      } else if (!uploadedFile.single) {
        newErrors[doc.id] = `${doc.name} is required`;
      }
    });

    setDocumentErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [requiredDocuments, uploadedFiles]);

  const handleNext = useCallback(() => {
    if (activeStep === 0) {
      if (!acknowledged) return;
      setActiveStep(1);
    }
  }, [activeStep, acknowledged]);

  const handleBack = useCallback(() => {
    if (isSubmitting) return;

    if (activeStep === 0) {
      navigate(-1);
    } else {
      setActiveStep(0);
    }
  }, [isSubmitting, activeStep]);

  const uploadedDocsCacheRef = useRef({});

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    if (!validateDocuments()) return;

    setIsSubmitting(true);
    try {
      const uploadedDocuments = await uploadAllPreOnboardingFiles(
        uploadedFiles,
        leadData?.mobileNumber,
        uploadedDocsCacheRef.current,
      );

      uploadedDocsCacheRef.current = uploadedDocuments;

      const documentsPayload = transformDocumentsToPayload(
        uploadedFiles,
        uploadedDocuments,
      );

      const payload = {
        financierType,
        financierInternal: leadData?.financierInternal || null,
        leadId: leadData?.leadId,
        dealer: leadData?.dealer,
        driverDetails: {
          driverName: leadData?.driverName || "",
          mobileNumber: leadData?.mobileNumber || leadData?.phoneNumber || "",
          alternateMobileNumber: leadData?.alternatePhoneNumber || "",
          residentialAddress:
            leadData?.residentialAddress || leadData?.address || "",
          age: leadData?.age || "",
          vehicleFinanced: leadData?.vehicleFinanced || "",
          city: leadData?.city || "",
        },
        vehicleFinancierDetails:
          leadData?.vehicleFinanced === "Yes"
            ? {
                name: leadData?.vehicleFinancierDetails?.name || "",
                phoneNumber:
                  leadData?.vehicleFinancierDetails?.phoneNumber || "",
                address: leadData?.vehicleFinancierDetails?.address || "",
              }
            : null,
        documents: documentsPayload,
        loanCalculationSummary: leadData?.loanSummary || null,
        createdBy: userEmail,
      };

      const response = await createDriverPreOnboarding(payload);
      const prn = response?.data?.prn || response?.prn;

      uploadedDocsCacheRef.current = {};

      setSuccessDialog({
        open: true,
        prn,
        driverName: leadData?.driverName || leadData?.name || "",
      });
      onSubmitSuccess?.({
        prn,
        leadId: leadData?.id,
        documents: documentsPayload,
      });
    } catch (error) {
      if (error?.partialResults) {
        uploadedDocsCacheRef.current = error.partialResults;
      }
      console.error("Lead document upload failed:", error);
      alert(
        error?.response?.data?.message ||
          error.message ||
          "Failed to submit documents",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    validateDocuments,
    uploadedFiles,
    leadData,
    financierType,
    userEmail,
    onSubmitSuccess,
  ]);

  const handleDocumentRemove = useCallback((documentId, side, index) => {
    setUploadedFiles((previous) => {
      const current = previous[documentId];

      if (!current) {
        return previous;
      }

      if (side === "multiple") {
        const multiple = Array.isArray(current.multiple)
          ? [...current.multiple]
          : [];

        if (index == null) {
          return previous;
        }

        multiple.splice(index, 1);

        return {
          ...previous,
          [documentId]: {
            ...current,
            multiple,
          },
        };
      }

      const { [side]: _removed, ...rest } = current;

      return {
        ...previous,
        [documentId]: rest,
      };
    });
  }, []);

  const handleCloseSuccess = useCallback(() => {
    setSuccessDialog({ open: false, prn: "", driverName: "" });
    navigate(`${paths.DRIVER_ONBOARDING}?tab=pre-onboarding`);
  }, []);

  if (isLoadingLead) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!leadResponse) {
    return (
      <ErrorState
        title="Lead Not Found"
        message="We couldn't find the lead information. Please check the lead ID and try again."
        onBack={handleBack}
        backDisabled={isSubmitting}
      />
    );
  }

  if (
    !financierType ||
    !Object.values(FINANCIER_TYPES).includes(financierType)
  ) {
    return (
      <ErrorState
        title="Invalid Financier Type"
        message="The financier type provided is invalid or missing. Please go back and select a valid financier type."
        iconBackgroundColor="#FFF7ED"
        iconColor="#EA580C"
        onBack={handleBack}
        backDisabled={isSubmitting}
      />
    );
  }

  return (
    <>
      {(isLeadFetchError || leadFetchError) && (
        <Typography sx={{ fontSize: 13, color: "#B3261E", mb: 2 }}>
          {(typeof leadFetchError === "string" && leadFetchError) ||
            leadFetchError?.message ||
            "Something went wrong while saving the lead."}
        </Typography>
      )}

      {activeStep === 0 && (
        <DocumentChecklist
          setActiveStep={setActiveStep}
          financierType={financierType}
          onBack={() => navigate(-1)}
        />
      )}

      {activeStep === 1 && (
        <EmiWrapperComponent
          title="Lead Document Upload"
          subTitle="Review required documents and upload them"
          icon={<FileCheck2 size={50} />}
          actions={[
            activeStep === 1 && (
              <BackButton onClick={handleBack} disabled={isSubmitting} />
            ),
            activeStep === 1 && (
              <Button
                key="submit"
                onClick={handleSubmit}
                variant="contained"
                size="large"
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Check />
                  )
                }
                disabled={isSubmitting}
                sx={{
                  bgcolor: "#059669",
                  "&:hover": { bgcolor: "#047857" },
                  "&:disabled": { bgcolor: "#94a3b8" },
                }}
              >
                {isSubmitting ? "Submitting..." : "Submit Documents"}
              </Button>
            ),
          ]}
        >
          <Box sx={{ py: { xs: 2, sm: 3 } }}>
            <DocumentSection
              financierType={financierType}
              context={documentContext}
              // uploadedFiles={uploadedFiles}
              onDocumentUpload={handleDocumentUpload}
              onDocumentRemove={handleDocumentRemove}
              documentErrors={documentErrors}
              uploadedFiles={uploadedFiles}
            />

            {isSubmitting && (
              <FullScreenLoader message="Uploading documents..." />
            )}
          </Box>

          <SuccessDialog
            open={successDialog.open}
            onClose={handleCloseSuccess}
            prn={successDialog.prn}
            driverName={successDialog?.driverName || ""}
          />
        </EmiWrapperComponent>
      )}
    </>
  );
});

export default LeadDocumentUpload;
